import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { hasDimensions, isSellable } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Minus, Plus } from 'lucide-react';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useVariantQuantitiesStore } from '@eliadmin/core/src/hooks/variant-quantities-store';
import { AddToCartButton } from '../../add-to-cart-button';

export function Quantity({ className, variantId }: { className?: string; variantId?: number }) {
  const product = useVariantsStore(s => s.product);
  const globalSelectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const setGlobalSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const openDialog = useDialogStore(s => s.open);

  // Use variant-specific quantity if variantId is provided, otherwise use global quantity
  const variantQuantitiesStore = useVariantQuantitiesStore();
  const selectedQuantity = variantId
    ? variantQuantitiesStore.getVariantQuantity(variantId, product.min_quantity ?? 1)
    : globalSelectedQuantity;
  const setSelectedQuantity = variantId
    ? (quantity: number) => variantQuantitiesStore.setVariantQuantity(variantId, quantity)
    : setGlobalSelectedQuantity;

  // For variant-specific quantities, use the variant itself; otherwise use selectedVariant or product
  const stockVariant = variantId
    ? product.variants?.find(v => v.id === variantId)
    : selectedVariant;
  const stock = stockVariant ?? product;

  const sellable = isSellable(stock, selectedQuantity);
  const variants = useVariantsStore(state => state.product.variants);
  const customValuesError = useVariantsStore(state => state.customValuesError);

  const disabled =
    (product.quantity < 1 && !sellable) ||
    (stockVariant && stockVariant?.quantity < 1 && !sellable) ||
    ((variants?.length || 0) > 0 && !variantId && !selectedVariant);

  const handleChangeQuantity = (value: number) => {
    if (!value) {
      value = 1;
    }
    const newValue = selectedQuantity + value;
    if (newValue <= 0) {
      setSelectedQuantity(1);
    } else {
      const sellableNext = isSellable(stock, newValue);
      if (!sellableNext && stock.quantity > 0) {
        openDialog({
          id: NotInStockDialog.id,
          content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit} />,
        });
        setSelectedQuantity(stock.quantity);
      } else if (sellableNext) {
        setSelectedQuantity(newValue);
      }
    }
  };

  return (
    <div
      className={cn('flex items-center justify-end', 'ea-variant-quantity__container', className)}
    >
      <div className="flex gap-2">
        <Button
          variant="outline"
          className={cn(
            'ea-variant-quantity__increase-quantity flex max-h-10 min-h-10 min-w-10 max-w-10 items-center justify-center p-0',
            'ea-variant-quantity__decrease-quantity',
            'ea-variant-quantity__button',
          )}
          disabled={selectedQuantity <= 1}
          onClick={() => {
            handleChangeQuantity(-1);
          }}
        >
          <Minus size={16} strokeWidth={3} color="black" />
        </Button>
        <div className="w-full max-w-20 flex-grow">
          <Input
            className={{
              wrapper:
                'ea-variant-quantity__input-wrapper flex max-h-10 min-h-10 w-full min-w-10 items-center justify-center rounded-lg bg-[#f5f5f5] text-center',
              main: 'h-10 w-full min-w-10 text-center',
            }}
            value={`${selectedQuantity}`}
            onChange={e => {
              const value = parseInt(e.target.value);
              if (value) {
                handleChangeQuantity(value - selectedQuantity);
              }
            }}
            onFocus={e => {
              e.target.select();
            }}
          />
        </div>
        <Button
          variant="outline"
          className={cn(
            'ea-variant-quantity__increase-quantity flex max-h-10 min-h-10 min-w-10 max-w-10 items-center justify-center p-0',
            'ea-variant-quantity__button',
          )}
          onClick={() => {
            handleChangeQuantity(1);
          }}
        >
          <Plus size={16} strokeWidth={3} color="black" />
        </Button>
      </div>
      <AddToCartButton
        className={cn('ml-4 min-w-10 max-w-10', 'ea-variant-quantity__add-to-cart-container')}
        quantity={selectedQuantity}
        disabled={disabled || (hasDimensions(product.attributes) ? customValuesError : false)}
        product={product}
        sellable={sellable}
        showIcon={true}
        variantId={variantId}
        onlyIcon
      />
    </div>
  );
}
